import { createFileRoute } from "@tanstack/react-router";
import { Brain, MailPlus, ScanLine } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/ai-brain-hero.webp";
import voiceImg from "@/assets/ai-brain-voice.webp";
import draftingImg from "@/assets/ai-brain-drafting.webp";

const IMG: Record<string, string> = { hero: heroImg, voice: voiceImg, drafting: draftingImg };

export const Route = createFileRoute("/vi/platform/ai")({
  head: () => ({
    meta: [
      { title: "AI Brain — banana.cards" },
      { name: "description", content: "Quét, ghi nhớ, làm giàu dữ liệu và follow-up — tự động. Bộ não thứ hai cho mọi cuộc trò chuyện." },
      { property: "og:title", content: "AI Brain — banana.cards" },
      { property: "og:description", content: "Quét, ghi nhớ, làm giàu dữ liệu và follow-up — tự động." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/ai", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="AI Brain"
      title="Chánh văn phòng"
      italic="trong túi bạn."
      description="Ghi lại mọi cuộc trò chuyện. Banana quét danh thiếp giấy, làm giàu liên hệ, tóm tắt cuộc họp và viết follow-up đúng giọng văn của bạn."
      heroImage={IMG['hero']}
      metrics={[{ v: "4 giờ", l: "tiết kiệm mỗi tuần" }, { v: "92%", l: "chấp nhận bản nháp" }, { v: "50+", l: "ngôn ngữ" }]}
      bullets={[
        { icon: ScanLine, title: "Quét & làm giàu", desc: "Chụp danh thiếp — Banana tạo liên hệ đầy đủ, không trùng lặp trong vài giây." },
        { icon: Brain, title: "Trí nhớ tích lũy", desc: "Hỏi CRM bằng tiếng Việt tự nhiên — Banana nhớ mọi chi tiết." },
        { icon: MailPlus, title: "Follow-up tự động", desc: "Email được AI viết, cá nhân hóa, đúng giọng của bạn — không bao giờ giống template." }
      ]}
      deepDives={[
        { eyebrow: "Giọng nói", title: "Trò chuyện với CRM.", italic: "Như với con người.", description: "Hỏi 'tuần này tôi chưa follow-up ai?' — Banana hiểu và hành động.", image: IMG["voice"], reverse: false, points: ["Lệnh ngôn ngữ tự nhiên", "Ghi âm tự gắn vào liên hệ", "Tóm tắt cuộc họp từ audio", "Hiểu đa ngôn ngữ"] },
        { eyebrow: "Soạn thảo", title: "Email cá nhân,", italic: "ở quy mô lớn.", description: "AI nháp phản hồi và follow-up dựa trên ngữ cảnh, tông giọng của bạn và hồ sơ người nhận — luôn cần bạn duyệt trước khi gửi.", image: IMG["drafting"], reverse: true, points: ["Khớp tông giọng từ email cũ", "Nhiều biến thể để chọn", "Tự lấy ngữ cảnh LinkedIn/web", "Cổng duyệt trước khi gửi"] }
      ]}
      howItWorks={[
        { title: "Kết nối hộp thư & lịch", desc: "Banana đọc ngữ cảnh, không gửi khi chưa có bạn." },
        { title: "Nói hoặc gõ", desc: "Hỏi bằng tiếng Việt hoặc dùng prompt bar." },
        { title: "Duyệt bản nháp", desc: "Xem nguồn và lý do trước khi duyệt." },
        { title: "Gửi — và ghi nhớ", desc: "Mọi tương tác huấn luyện bộ não riêng của bạn." }
      ]}
      capabilities={["Truy vấn CRM bằng ngôn ngữ tự nhiên", "Ghi chú & tóm tắt giọng nói", "Soạn follow-up bằng AI", "Tin nhắn 'nên liên hệ ai hôm nay'", "Gắn tag & xếp ưu tiên thông minh", "Hỗ trợ đa ngôn ngữ", "Tùy chỉnh giọng AI", "Tuân thủ GDPR"]}
      faq={[
        { q: "AI trong Banana làm gì cho tôi?", a: "Quét danh thiếp giấy, ghi âm và tóm tắt cuộc họp, soạn email follow-up cá nhân hóa, làm giàu liên hệ, gợi ý hành động tiếp theo, đánh giá lead, tạo nháp hợp đồng và dịch nội dung sang 50+ ngôn ngữ." },
        { q: "Tôi có thể huấn luyện AI nói giọng của tôi không?", a: "Có — AI Brain học từ tông giọng, value prop, dịch vụ và phong cách viết của bạn. Mỗi thành viên đội có thể thêm giọng cá nhân." },
        { q: "Dữ liệu của tôi có dùng để huấn luyện mô hình AI không?", a: "Không. Mọi dữ liệu là riêng tư trong workspace, mã hóa khi lưu, và không bao giờ chia sẻ với bên thứ ba." },
        { q: "AI hoạt động bằng tiếng Việt không?", a: "Có — soạn, tóm tắt, ghi âm, phân loại và làm giàu dữ liệu đều hoạt động hoàn hảo với tiếng Việt và 50+ ngôn ngữ khác." }
      ]}
      outro={{ title: "Ngừng gõ ghi chú.", italic: "Bắt đầu chốt deal.", description: "Để AI lo phần việc lặt vặt — bạn lo quan hệ." }}
    />
  );
}